<template>
    <div class="go-ahead flow__container-fixed flow__panel">
        <section class="go-ahead__top">
            <h2 class="go-ahead__top__title">
                Renovation Plan
            </h2>
            <property-info class="go-ahead__top__property-info"
                           :property="property"
                           pre-text="for" />
            <section class="go-ahead__main-area">
                <error-handler class="go-ahead__errors" />
                <div>
                    <p class="go-ahead__flow-message">
                        {{ endOfPlanBuildFlowMessage }}
                    </p>
                    <p v-if="isServicedPropertyUnderNationalPresentationScheme" class="go-ahead__national-message">
                        {{ myResources.additionalServices }}
                    </p>
                </div>
                <renovation-info v-if="renovation"
                                 class="go-ahead__info"
                                 :renovation="renovation" />
                <loading-animation v-else class="go-ahead__loading">
                    Loading renovation plan&hellip;
                </loading-animation>
            </section>
        </section>
        <aside class="go-ahead__aside">
            <div class="go-ahead__buttons">
                <cta-tile class="go-ahead__aside__buttons__button --dashboard"
                          name="go-ahead-button--dashboard"
                          :button-text="allResources.views.dashboard.title"
                          :breakout-extended="true"
                          icon-class="fas fa-house-user"
                          icon-location="pre"
                          @click="$router.push({ name: 'Dashboard' })"
                          :secondary-button-text="showExternalUserRedirect ? myResources.externalUsers.submissionRedirectLinkText : ''"
                          secondary-button-icon-class="fas fa-house-user"
                          secondary-button-icon-location="post"
                          @secondary-click="onSubmissionRedirectLinkClick" />
            </div>
        </aside>
    </div>
</template>

<script>

import Vuex from 'vuex'

import Project from '@/models/project'

import strings from '@/helpers/strings'

import RenovationsManager from '@/renovations-manager'
import UserManager from '@/user-manager'

import AnalyticsPage from './analytics-page'
import InstanceTypeHelper from '@/components/instance-type-helper'
import ResourcesHelper from '@/components/resources-helper'

import CtaTile from '@/components/app-core/cta-tile'
import ErrorHandler from '@/components/app-core/error-handler'
import PropertyInfo from '@/components/app-core/property-info'
import LoadingAnimation from '@/components/app-core/loading-animation'
import RenovationInfo from '@/components/go-ahead/renovation-info'

import Logger from '@/logger'

export default {

    name: 'GoAheadPage',

    mixins: [
        AnalyticsPage,
        InstanceTypeHelper,
        ResourcesHelper,
    ],

    data() {
        return {
            /**
             * @type {?Renovation}
             */
            renovation: null,

            /**
             * @type {?string}
             */
            errorMessage: null,

            loggingCategory: 'Go Ahead Page',
        }
    },

    computed: {

        ...Vuex.mapState({
            /**
             * @param {VuexState} stateStore
             * @returns {Project}
             */
            currentProject: stateStore => Project.ensure(stateStore.improvements.currentProject),

            /**
             * @param {VuexState} stateStore
             * @returns {?PropertyAsJson}
             */
            property: stateStore => stateStore.property,
        }),

        /**
         * @returns {string}
         */
        endOfPlanBuildFlowMessage() {
            return this.scheme.isUmbrella
                ? this.servicingScheme !== null
                    ? this.servicingScheme.title !== this.scheme.title
                        ? strings.parse(this.myResources.confirmationServicingScheme, this)
                        : this.myResources.confirmationInTouch
                    : this.myResources.confirmationSent
                : this.scheme.userOnlyTool
                    ? this.myResources.confirmationSent
                    : this.myResources.confirmationInTouch
        },

        /**
         * @returns {boolean}
         */
        isServicedPropertyUnderNationalPresentationScheme() {
            return this.scheme.isNational && this.servicingScheme !== null
        },

        /**
         * Return the resources object for this page.
         * @return {GoAheadResources}
         */
        myResources() {
            return this.parseResources('views.goAhead')
        },

        /**
        * True if the external user redirect button should be displayed.
        * @return {boolean}
        */
        showExternalUserRedirect() {
            if (!this.myResources.externalUsers)
                return false

            const linkUrl = this.myResources.externalUsers.submissionRedirectLinkUrl
            return linkUrl && linkUrl !== '' && UserManager.isExternalUser()
        },
    },

    watch: {

        async currentProject(newValue, oldValue) {
            const projectHasBeenInitialised = (!oldValue || !oldValue.initialised) && newValue && newValue.initialised
            Logger.logObject({ oldValue, newValue, projectHasBeenInitialised }, 'currentProject watch called', this.loggingCategory)
            if (projectHasBeenInitialised) {
                try {
                    this.renovation = (await RenovationsManager.submitAsync()).data
                } catch (err) {
                    Logger.error(err, this.loggingCategory)
                    this.errorMessage = err
                }
            }
        },
    },

    async mounted() {
        try {
            if (this.currentProject.initialised) {
                Logger.log('Submitting plan', this.loggingCategory)
                this.renovation = (await RenovationsManager.submitAsync()).data
                this.trackEvent('plan_build', 'plan_submitted')
            }
        } catch (err) {
            Logger.error(err, this.loggingCategory)
            this.errorMessage = err
        }
        this.trackEvent('dashboard', 'dashboard_loaded')
    },

    methods: {

        /**
        * Click handler for the external user redirect button.
        */
        onSubmissionRedirectLinkClick() {
            window.location.href = this.myResources.externalUsers.submissionRedirectLinkUrl
        },
    },

    components: {
        CtaTile,
        ErrorHandler,
        PropertyInfo,
        RenovationInfo,
        LoadingAnimation,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\views\_go-ahead.scss

.go-ahead {

    @include element('top') {
        width: 100%;
    }

    @include element('flow-message') {
        margin: 0 rem(10px);

        @include bp-min-wide {
            margin: 0;
        }
    }

    @include element('info') {
        padding-bottom: 6.5rem;

        @include bp-min-small {
            padding-bottom: 3.5rem;
        }

        @include bp-min-mid {
            width: rem(500px);
            margin: 5.8rem 0;
        }

        @include bp-min-wide {
            margin-top: 4rem;
            margin-bottom: 2rem;
        }
    }

    @include element('loading') {
        margin-top: 1rem;
    }
}

</style>
